<template>
  <div class="product-list" @scroll="handleScroll" ref="scroller">
    <div v-if="visibleItems.length === 0" class="product-card placeholder-card">
      <div class="ThumbnailCarouselPlaceholder">
        <small>No items</small>
      </div>
    </div>
    <div
      class="product-card"
      :class="{
        'product-card-favourites': favourites,
      }"
      v-for="item in visibleItems"
      :key="item.id"
    >
      <ThumbnailCarousel :product="item" :favourites="favourites" />

      <!-- Title and delete button row -->
      <div v-if="favourites" class="title-delete-row divMicroMarginTopBottom">
        <div v-if="item.itemName" class="item-title">
          <small
            ><strong>{{ item.itemName }}</strong></small
          >
        </div>
        <div
          class="wishlist-remove-button"
          @click="handleDeleteClick(item)"
          :class="{ 'confirm-phase': itemToConfirmDelete === item.id }"
        >
          <template v-if="itemToConfirmDelete === item.id">
            <small class="delete-confirm-text"
              >Remove? <strong>Yes</strong></small
            >
          </template>
          <template v-else>
            <svg class="tinyImg">
              <use href="@/assets/icons/iconset.svg#trash_tiny"></use>
            </svg>
          </template>
        </div>
      </div>

      <!-- Title only (when not in favourites mode) -->
      <div v-else-if="item.itemName" class="item-title divMicroMarginTopBottom">
        <small
          ><strong>{{ item.itemName }}</strong></small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";

import { ref, computed, onMounted } from "vue";
import { auth } from "@/firebase";
import {
  db,
  doc,
  getDocs,
  deleteDoc,
  setDoc,
  serverTimestamp,
  collection,
  query,
} from "@/services/legacyBrowserPersistenceBoundary";
import { toast } from "vue3-toastify";

import ThumbnailCarousel from "@/components/DashboardComponents/EcosystemComponents/ProductListComponents/ThumbnailCarousel.vue";

export default {
  name: "ScrollableMarketplaceList",
  props: {
    sortedProductsAndServices: {
      type: Array,
      required: true,
      default: () => [],
    },
    favourites: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ThumbnailCarousel,
  },

  setup(props, { emit }) {
    const store = useStore();

    const numberOfItemsToShow = ref(10);
    const scroller = ref(null);
    const currentUserUid = ref(auth.currentUser ? auth.currentUser.uid : null);
    const isWishlisted = ref({});
    const itemToConfirmDelete = ref(null);

    const handleDeleteClick = (item) => {
      if (itemToConfirmDelete.value === item.id) {
        // Second click - confirm delete
        removeFromWishlist(item);
        itemToConfirmDelete.value = null;
      } else {
        // First click - show confirmation
        itemToConfirmDelete.value = item.id;
        // Reset after 3 seconds if no confirmation
        setTimeout(() => {
          if (itemToConfirmDelete.value === item.id) {
            itemToConfirmDelete.value = null;
          }
        }, 3000);
      }
    };

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scroller.value;
      if (scrollLeft + clientWidth >= scrollWidth - 100) {
        numberOfItemsToShow.value += 10;
      }
    };

    const addToCart = async (item) => {
      toast.error("Feature is under development.", {
        autoClose: 3000,
      });
    };

    const removeFromWishlist = async (item) => {
      if (currentUserUid.value) {
        try {
          const wishlistDocRef = doc(
            db,
            "users",
            currentUserUid.value,
            "Wishlist",
            item.id
          );
          await deleteDoc(wishlistDocRef);
          isWishlisted.value[item.id] = false;
          toast.success("Removed from favourites.", { autoClose: 3000 });
          emit("favouritesUpdated");
        } catch (error) {
          isWishlisted.value[item.id] = true;
          console.error("Error removing from favourites: ", error);
          toast.error("Error removing from favourites.", { autoClose: 3000 });
        }
      } else {
        toast.error("You must be logged in to remove items from favourites.", {
          autoClose: 3000,
        });
      }
    };

    const addToWishlist = async (item) => {
      if (currentUserUid.value) {
        try {
          const wishlistDocRef = doc(
            db,
            "users",
            currentUserUid.value,
            "Wishlist",
            item.id
          );
          await setDoc(wishlistDocRef, {
            ...item,
            createdAt: serverTimestamp(),
          });
          isWishlisted.value[item.id] = true;
          emit("favouritesUpdated");
          toast.success("Added to favourites.", { autoClose: 3000 });
        } catch (error) {
          console.error("Error adding to favourites: ", error);
          toast.error("Error adding to favourites.", { autoClose: 3000 });
        }
      } else {
        toast.error("You must be logged in to add items to favourites.", {
          autoClose: 3000,
        });
      }
    };

    const getCurrencySymbol = (item) => {
      return item.coinSymbol || "Ʉ";
    };

    onMounted(async () => {
      // Initialize isWishlisted for favourites functionality
      if (currentUserUid.value) {
        const q = query(
          collection(db, "users", currentUserUid.value, "Wishlist")
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          isWishlisted.value[doc.id] = true;
        });
      }
    });

    const visibleItems = computed(() => {
      return props.sortedProductsAndServices
        .filter((item) => {
          // Allow media items to pass through even if org details are missing
          if (item.type === "media" || item.mediaType) return true;
          const orgDetails = store.getters.getSellerOrgDetails(
            item.organisation
          );
          // If we have org details ensure membersCount > 0; if not, still allow (lenient) so items don't vanish
          if (!orgDetails) return true;
          return orgDetails.membersCount > 0;
        })
        .map((item) => {
          // Only infer type for legacy product/service docs that don't already declare media
          if (!item.type || (item.type !== "media" && !item.mediaType)) {
            if (item.availability) {
              item.type = "service";
            } else if (item.existingStock !== undefined) {
              item.type = "product";
            }
          }
          return item;
        })
        .slice(0, numberOfItemsToShow.value);
    });

    return {
      visibleItems,
      handleScroll,
      scroller,
      removeFromWishlist,
      addToWishlist,
      itemToConfirmDelete,
      handleDeleteClick,
    };
  },
};
</script>

<style scoped>
.product-list {
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  scroll-snap-type: x mandatory;
  box-shadow: var(--shadow-surface-recessed);
  border-radius: var(--radius-md);
  height: auto;
  margin-bottom: var(--space-4);
}
.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  scroll-snap-align: start;
  width: 25%;
  padding: var(--space-3) var(--space-3) var(--space-4)
    var(--space-3);
  height: auto;
  border-radius: var(--radius-sm);
}

.productActions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.product-card-favourites {
  width: 20%;
  padding-bottom: var(--space-1) !important;
}
.yourProduct {
  opacity: 0.6;
}

.ThumbnailCarouselPlaceholder {
  position: relative;
  width: 100%;
  display: flex;
  height: 14vh;
  overflow: hidden;
  border-radius: var(--radius-sm);
}

.actionsRow {
  align-items: center;
}
.firstProdDescRow {
  align-items: baseline;
}

.innerSticker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--control-height-dense);

  height: var(--control-height-dense);
  border-radius: 50%;
  background-color: var(--color-surface-subtle);
  padding: var(--space-2);
  cursor: pointer;
}

.product-card {
  position: relative;
}

/* Title and delete button row */
.title-delete-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  width: 100%;
  height: var(--control-height-micro);
}

.item-title {
  flex: 1;
  min-width: 0; /* Allows text-overflow to work */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-title small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.wishlist-remove-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease, padding 0.2s ease;
}

.wishlist-remove-button:hover {
  opacity: 0.7;
}

.wishlist-remove-button:active {
  transform: scale(0.92);
}

.wishlist-remove-button.confirm-phase {
  padding: 0 var(--space-1);
}

.delete-confirm-text {
  color: var(--color-feedback-danger-foreground);
  white-space: nowrap;
}

.delete-confirm-text strong {
  cursor: pointer;
}

.favourites-button {
  border: transparent !important;
  padding: 2px 0 2px 4px !important;
}

.ownedByCurrentUser .innerSticker {
  width: fit-content;
  border-radius: var(--radius-pill);
  gap: var(--space-2);
  padding: 0 var(--space-4);
}

.descCont {
  margin-top: var(--space-4);
  margin-bottom: var(--space-4);
  overflow-wrap: break-word;
  white-space: normal;
}

.stockWarningColor {
  color: var(--color-feedback-danger-foreground);
}
.grayedOut,
.soldout,
.service-label {
  color: var(--color-text-muted);
}

@media (max-width: 590px) {
  .product-card {
    width: 50%;
    padding: var(--space-3) var(--space-3) var(--space-4)
      var(--space-3);
  }
  .product-card-favourites {
    width: 33%;
  }
}
</style>
